import { components, useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";

type CurrentUserPermissions = components["schemas"]["CurrentUserPermissions"];

const DEFAULT_PERMISSIONS: CurrentUserPermissions = {
  is_admin: false,
  is_budget_admin: false,
  is_trader: false,
  is_trader_admin: false
};

/**
 * Fetches the current user's server-computed permission flags. Used by the
 * UI to enable/disable role-gated controls (e.g. the Unfreeze button on
 * the budget pages, the AdminGuard route).
 *
 * Roles live in the in-app store and can change while a session is open,
 * so this follows the global staleTime (5 min) instead of caching for the
 * session. While the query is pending, the flags are `false` and
 * `isLoading` is `true`; consumers should render a loading state rather
 * than acting on the default-deny flags.
 */
export function useCurrentUserPermissions() {
  const apiClient = useApi();

  return useQuery({
    queryKey: ["currentUserPermissions"],
    meta: {
      errorMessage: "Failed to load user permissions"
    },
    queryFn: async () => {
      const response = await apiClient.GET("/v2/auth/me/permissions", {});
      return response.data?.result ?? DEFAULT_PERMISSIONS;
    }
  });
}
